import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";

function MeasuredVirtualRow({ itemKey, top, onMeasure, children }) {
  const rowRef = useRef(null);

  useEffect(() => {
    const node = rowRef.current;
    if (!node) return undefined;
    const measure = () => onMeasure(itemKey, node.getBoundingClientRect().height);
    measure();
    if (typeof ResizeObserver === "undefined") {
      window.addEventListener("resize", measure);
      return () => window.removeEventListener("resize", measure);
    }
    const observer = new ResizeObserver(measure);
    observer.observe(node);
    return () => observer.disconnect();
  }, [itemKey, onMeasure]);

  return (
    <div ref={rowRef} style={{ position: "absolute", top, left: 0, right: 0, paddingBottom: 8, boxSizing: "border-box" }}>
      {children}
    </div>
  );
}

export default function VirtualizedList({
  items,
  getKey,
  renderItem,
  height = 600,
  estimatedItemHeight = 180,
  overscan = 4,
  testId,
  resetKey,
}) {
  const viewportRef = useRef(null);
  const [scrollTop, setScrollTop] = useState(0);
  const [measuredHeights, setMeasuredHeights] = useState({});

  useEffect(() => {
    if (viewportRef.current) viewportRef.current.scrollTop = 0;
    setScrollTop(0);
  }, [resetKey]);

  const layout = useMemo(() => {
    const offsets = [];
    let totalHeight = 0;
    items.forEach((item, index) => {
      const key = String(getKey(item));
      offsets.push(totalHeight);
      totalHeight += measuredHeights[key] || estimatedItemHeight;
    });
    return { offsets, totalHeight };
  }, [estimatedItemHeight, getKey, items, measuredHeights]);

  const onMeasure = useCallback((key, measuredHeight) => {
    if (!measuredHeight) return;
    setMeasuredHeights((current) => (
      Math.abs((current[key] || 0) - measuredHeight) < 1
        ? current
        : { ...current, [key]: measuredHeight }
    ));
  }, []);

  let start = 0;
  while (start < items.length && layout.offsets[start] + (measuredHeights[String(getKey(items[start]))] || estimatedItemHeight) < scrollTop) {
    start += 1;
  }
  start = Math.max(0, start - overscan);
  let end = start;
  const viewportHeight = Math.min(height, layout.totalHeight);
  const viewportBottom = scrollTop + viewportHeight;
  while (end < items.length && layout.offsets[end] < viewportBottom) end += 1;
  end = Math.min(items.length, end + overscan);

  const onScroll = (event) => setScrollTop(event.currentTarget.scrollTop);

  return (
    <div
      ref={viewportRef}
      onScroll={onScroll}
      style={{ height: viewportHeight, overflowY: "auto", overflowAnchor: "none" }}
      data-testid={testId}
    >
      <div style={{ height: layout.totalHeight, position: "relative" }}>
        {items.slice(start, end).map((item, offset) => {
          const index = start + offset;
          const key = String(getKey(item));
          return (
            <MeasuredVirtualRow key={key} itemKey={key} top={layout.offsets[index]} onMeasure={onMeasure}>
              {renderItem(item)}
            </MeasuredVirtualRow>
          );
        })}
      </div>
    </div>
  );
}
